import React, { useState } from 'react';
import { Loader2, RefreshCw, Activity } from 'lucide-react';
import { toast } from 'react-hot-toast';

export default function LiveToday() {
  const [loadingGads, setLoadingGads] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loadingGa4, setLoadingGa4] = useState(false);
  const [loadingAdjust, setLoadingAdjust] = useState(false);
  const [gadsResult, setGadsResult] = useState(null);
  const [metaResult, setMetaResult] = useState(null);
  const [ga4Result, setGa4Result] = useState(null);
  const [adjustResult, setAdjustResult] = useState(null);
  const [errorGads, setErrorGads] = useState('');
  const [errorMeta, setErrorMeta] = useState('');
  const [errorGa4, setErrorGa4] = useState('');
  const [errorAdjust, setErrorAdjust] = useState('');

  const getBrandId = () =>
    localStorage.getItem('currentBrandId') ||
    localStorage.getItem('selectedBrandId') ||
    sessionStorage.getItem('selectedBrandId') ||
    '';

  const fetchLive = async (platform) => {
    const brandId = getBrandId();
    if (!brandId) {
      toast.error('Please select a brand first');
      return;
    }
    const isGads = platform === 'GOOGLE_ADS';
    const isGa4 = platform === 'GOOGLE_ANALYTICS';
    const isAdjust = platform === 'ADJUST';
    try {
      if (isGads) {
        setLoadingGads(true); setErrorGads(''); setGadsResult(null);
      } else if (isGa4) {
        setLoadingGa4(true); setErrorGa4(''); setGa4Result(null);
      } else if (isAdjust) {
        setLoadingAdjust(true); setErrorAdjust(''); setAdjustResult(null);
      } else {
        setLoadingMeta(true); setErrorMeta(''); setMetaResult(null);
      }

      let endpoint = '';
      if (isGads) {
        endpoint = `https://api.glowytics.com/api/brands/${brandId}/live/google-ads/today`;
      } else if (isGa4) {
        endpoint = `https://api.glowytics.com/api/brands/${brandId}/live/ga4/today`;
      } else if (isAdjust) {
        endpoint = `https://api.glowytics.com/api/brands/${brandId}/live/adjust/today`;
      } else {
        endpoint = `https://api.glowytics.com/api/brands/${brandId}/live/meta-ads/today`;
      }

      const res = await fetch(endpoint, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-Brand-ID': brandId
        }
      });

      const contentType = res.headers.get('content-type') || '';
      const payload = contentType.includes('application/json') ? await res.json() : await res.text();

      if (!res.ok) {
        const msg = (payload && (payload.error || payload.message)) || `HTTP ${res.status}`;
        if (isGads) setErrorGads(msg);
        else if (isGa4) setErrorGa4(msg);
        else if (isAdjust) setErrorAdjust(msg);
        else setErrorMeta(msg);
        toast.error(msg);
        return;
      }

      if (isGads) setGadsResult(payload);
      else if (isGa4) setGa4Result(payload);
      else if (isAdjust) setAdjustResult(payload);
      else setMetaResult(payload);
      toast.success(isGads ? 'Google Ads live fetched' : isGa4 ? 'GA4 live fetched' : isAdjust ? 'Adjust live fetched' : 'Meta Ads live fetched');
    } catch (e) {
      const msg = e?.message || 'Request failed';
      if (isGads) setErrorGads(msg);
      else if (isGa4) setErrorGa4(msg);
      else if (isAdjust) setErrorAdjust(msg);
      else setErrorMeta(msg);
      toast.error(msg);
    } finally {
      if (isGads) setLoadingGads(false);
      else if (isGa4) setLoadingGa4(false);
      else if (isAdjust) setLoadingAdjust(false);
      else setLoadingMeta(false);
    }
  };

  const JsonBlock = ({ data }) => {
    if (!data) return null;
    return (
      <pre className="text-xs bg-gray-900 text-green-200 rounded-lg p-3 overflow-auto max-h-[420px]">
{JSON.stringify(data, null, 2)}
      </pre>
    );
  };

  const ErrorBlock = ({ message }) => {
    if (!message) return null;
    return (
      <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
        {message}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <Activity className="w-5 h-5 text-indigo-600" />
          <h1 className="text-xl font-semibold">Labs – Live Ads Today (No DB Write)</h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Google Ads Card */}
          <div className="bg-white rounded-xl border p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-medium">Google Ads — Live (today)</h2>
                <p className="text-sm text-gray-500">Fetches today&apos;s performance directly from API.</p>
              </div>
              <button
                onClick={() => fetchLive('GOOGLE_ADS')}
                disabled={loadingGads}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white ${loadingGads ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}
              >
                {loadingGads ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {loadingGads ? 'Fetching…' : 'Fetch Live'}
              </button>
            </div>
            <ErrorBlock message={errorGads} />
            <JsonBlock data={gadsResult} />
          </div>

          {/* Meta Ads Card */}
          <div className="bg-white rounded-xl border p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-medium">Meta Ads — Live (today)</h2>
                <p className="text-sm text-gray-500">Fetches today&apos;s performance directly from API.</p>
              </div>
              <button
                onClick={() => fetchLive('META_ADS')}
                disabled={loadingMeta}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white ${loadingMeta ? 'bg-gray-400' : 'bg-purple-600 hover:bg-purple-700'}`}
              >
                {loadingMeta ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {loadingMeta ? 'Fetching…' : 'Fetch Live'}
              </button>
            </div>
            <ErrorBlock message={errorMeta} />
            <JsonBlock data={metaResult} />
          </div>
        </div>

        {/* GA4 Card */}
        <div className="mt-6 grid grid-cols-1">
          <div className="bg-white rounded-xl border p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-medium">GA4 — Live (today)</h2>
                <p className="text-sm text-gray-500">Sessions, Purchases and Revenue (today, live).</p>
              </div>
              <button
                onClick={() => fetchLive('GOOGLE_ANALYTICS')}
                disabled={loadingGa4}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white ${loadingGa4 ? 'bg-gray-400' : 'bg-emerald-600 hover:bg-emerald-700'}`}
              >
                {loadingGa4 ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {loadingGa4 ? 'Fetching…' : 'Fetch Live'}
              </button>
            </div>
            <ErrorBlock message={errorGa4} />
            <JsonBlock data={ga4Result} />
          </div>
        </div>

        {/* Adjust Card */}
        <div className="mt-6 grid grid-cols-1">
          <div className="bg-white rounded-xl border p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-medium">Adjust — Live (today)</h2>
                <p className="text-sm text-gray-500">Sessions, Installs, Purchases and Revenue (today, live).</p>
              </div>
              <button
                onClick={() => fetchLive('ADJUST')}
                disabled={loadingAdjust}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white ${loadingAdjust ? 'bg-gray-400' : 'bg-gray-700 hover:bg-gray-800'}`}
              >
                {loadingAdjust ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {loadingAdjust ? 'Fetching…' : 'Fetch Live'}
              </button>
            </div>
            <ErrorBlock message={errorAdjust} />
            <JsonBlock data={adjustResult} />
          </div>
        </div>

        <div className="mt-6 text-xs text-gray-500">
          Note: This page calls live endpoints intended to query platforms directly and not write to the database.
        </div>
      </div>
    </div>
  );
}


